import React, { useState, useEffect } from 'react';
import {
  FaPaperPlane, FaTimes, FaPlus, FaTrash,
  FaCheckCircle, FaExclamationCircle, FaCalendarAlt,
  FaSpinner, FaEnvelope, FaInfoCircle, FaRedo,
  FaTimesCircle,
} from 'react-icons/fa';
import { envoyerLiens, relancerLien, getSession } from '../components/RecueilApi';

const SendRecueilModal = ({ session, onClose, onSuccess }) => {
  const [tab,       setTab]       = useState(0);
  const [emails,    setEmails]    = useState(['']);
  const [expireAt,  setExpireAt]  = useState('');
  const [loading,   setLoading]   = useState(false);
  const [results,   setResults]   = useState(null);
  const [errors,    setErrors]    = useState({});
  const [globalErr, setGlobalErr] = useState(null);
  const [liens,     setLiens]     = useState([]);
  const [loadLiens, setLoadLiens] = useState(false);
  const [relId,     setRelId]     = useState(null);
  const [relMsg,    setRelMsg]    = useState(null);

  // Charger les liens existants pour l'onglet Suivi
  const refreshLiens = async () => {
    setLoadLiens(true);
    try {
      const data = await getSession(session.id);
      setLiens(data.liens || []);
    } catch { setLiens([]); }
    finally { setLoadLiens(false); }
  };

  useEffect(() => { if (tab === 1) refreshLiens(); }, [tab]);

  //  Gestion emails 
  const addEmail    = () => setEmails(e => [...e, '']);
  const removeEmail = (i) => setEmails(e => e.filter((_, idx) => idx !== i));
  const updateEmail = (i, v) => {
    setEmails(e => e.map((em, idx) => idx === i ? v : em));
    if (errors[i]) setErrors(e => { const n={...e}; delete n[i]; return n; });
  };

  // Coller plusieurs emails (un par ligne ou séparés par virgule/point-virgule)
  const handlePasteArea = (e) => {
    const text = e.clipboardData.getData('text');
    const parsed = text
      .split(/[\n,;]+/)
      .map(s => s.trim())
      .filter(s => s.includes('@'));
    if (parsed.length > 1) {
      e.preventDefault();
      setEmails(parsed);
    }
  };

  //  Validation 
  const valid = () => {
    const e = {};
    emails.forEach((em, i) => {
      if (!em.trim() || !/\S+@\S+\.\S+/.test(em.trim())) e[i] = true;
    });
    setErrors(e);
    return !Object.keys(e).length;
  };

  //  Envoi ─
  const send = async () => {
    if (!valid()) return;
    setLoading(true); setGlobalErr(null);
    try {
      const validEmails = emails.map(e => e.trim().toLowerCase()).filter(Boolean);
      const data = await envoyerLiens(session.id, validEmails, expireAt || null);
      setResults(data);
      if (onSuccess) onSuccess();
    } catch (err) { setGlobalErr(err.message || "Erreur lors de l'envoi."); }
    finally { setLoading(false); }
  };

  //  Relance ─
  const relance = async (id) => {
    setRelId(id); setRelMsg(null);
    try {
      await relancerLien(session.id, id);
      setRelMsg({ id, t:'ok', m:'Relance envoyée !' });
      await refreshLiens();
    } catch { setRelMsg({ id, t:'err', m:'Erreur relance' }); }
    finally { setRelId(null); }
  };

  //  Badge statut 
  const badge = (s) => {
    const m = {
      envoye: { c:'#0d6efd', b:'rgba(13,110,253,.1)',  l:'Envoyé'    },
      ouvert: { c:'#fd7e14', b:'rgba(253,126,20,.1)',  l:'Ouvert'    },
      soumis: { c:'#198754', b:'rgba(25,135,84,.1)',   l:'Soumis ✓'  },
    };
    const d = m[s] || { c:'#718096', b:'rgba(113,128,150,.1)', l:'En attente' };
    return <span style={{ fontSize:12, fontWeight:700, color:d.c, background:d.b, padding:'3px 10px', borderRadius:20 }}>{d.l}</span>;
  };

  return (
    <>
      <style>{`
        .smo{position:fixed;inset:0;background:rgba(15,23,42,.65);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;z-index:3000;padding:20px;animation:smof .2s ease}
        @keyframes smof{from{opacity:0}to{opacity:1}}
        .smm{background:#fff;border-radius:22px;width:100%;max-width:680px;max-height:92vh;display:flex;flex-direction:column;box-shadow:0 24px 64px rgba(0,0,0,.22);overflow:hidden;animation:smop .26s cubic-bezier(.34,1.56,.64,1)}
        @keyframes smop{from{opacity:0;transform:scale(.92) translateY(16px)}to{opacity:1;transform:none}}
        .smtab{padding:13px 20px;font-size:14px;font-weight:700;color:#a0aec0;border:none;background:transparent;cursor:pointer;border-bottom:3px solid transparent;margin-bottom:-2px;transition:all .2s;font-family:inherit}
        .smtab.on{color:#fd7e14;border-bottom-color:#fd7e14}
        .smtab:hover:not(.on){color:#374151}
        .email-row{display:flex;align-items:center;gap:8px;margin-bottom:8px}
        .email-inp{flex:1;height:44px;padding:0 14px;border:2px solid #e2e8f0;border-radius:10px;font-size:14px;font-family:inherit;color:#1a202c;outline:none;background:#fff;transition:border-color .2s}
        .email-inp:focus{border-color:#fd7e14;box-shadow:0 0 0 4px rgba(253,126,20,.1)}
        .email-inp.err{border-color:#dc3545}
        .email-inp.err:focus{border-color:#dc3545;box-shadow:0 0 0 4px rgba(220,53,69,.1)}
        .sm-add{display:flex;align-items:center;gap:8px;padding:9px 18px;border:2px dashed #e2e8f0;border-radius:10px;background:transparent;cursor:pointer;font-size:13.5px;font-weight:600;color:#718096;font-family:inherit;transition:all .2s;margin-top:6px}
        .sm-add:hover{border-color:#fd7e14;color:#fd7e14;background:rgba(253,126,20,.04)}
        .sm-del{width:36px;height:44px;border-radius:9px;border:2px solid #e2e8f0;background:transparent;display:flex;align-items:center;justify-content:center;color:#dc3545;cursor:pointer;transition:all .2s;flex-shrink:0}
        .sm-del:hover:not(:disabled){background:rgba(220,53,69,.06);border-color:#dc3545}
        .sm-del:disabled{opacity:.3;cursor:not-allowed}
        .lien-row{display:flex;align-items:center;gap:12px;padding:12px 16px;border-radius:12px;border:1px solid #e2e8f0;margin-bottom:8px;transition:background .15s}
        .lien-row:hover{background:#fafafa}
        .smspin{animation:smspinn .8s linear infinite}
        @keyframes smspinn{to{transform:rotate(360deg)}}
      `}</style>

      <div className="smo" onClick={onClose}>
        <div className="smm" onClick={e => e.stopPropagation()}>

          {/*  HEADER ─ */}
          <div style={{ padding:'20px 24px 0', flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:4 }}>
              <div>
                <div style={{ fontSize:19, fontWeight:900, color:'#1a202c', marginBottom:3 }}>
                  Envoyer les liens de recueil
                </div>
                <div style={{ fontSize:13, color:'#718096', lineHeight:1.5 }}>
                  <strong style={{ color:'#374151' }}>{session.titre}</strong>
                  {session.dates && <span style={{ marginLeft:10, color:'#a0aec0' }}>· {session.dates}</span>}
                  {session.lieu  && <span style={{ marginLeft:6,  color:'#a0aec0' }}>· {session.lieu}</span>}
                </div>
              </div>
              <button onClick={onClose} style={{ width:36, height:36, borderRadius:9, border:'2px solid #e2e8f0', background:'transparent', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#718096', marginLeft:12, flexShrink:0 }}>
                <FaTimes />
              </button>
            </div>

            {/* Tabs */}
            <div style={{ display:'flex', borderBottom:'2px solid #f0f4f8', marginTop:16 }}>
              {['Envoyer les liens', 'Suivi des envois'].map((t, i) => (
                <button key={i} className={`smtab${tab===i?' on':''}`}
                  onClick={() => { setTab(i); setResults(null); setGlobalErr(null); }}>
                  {t}
                  {i===1 && liens.length > 0 && (
                    <span style={{ marginLeft:7, fontSize:11, fontWeight:800, background:'rgba(253,126,20,.1)', color:'#fd7e14', padding:'2px 7px', borderRadius:20 }}>
                      {liens.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/*  BODY ─ */}
          <div style={{ overflowY:'auto', flex:1, padding:'22px 24px' }}>

            {/* TAB 0 — Envoyer liens */}
            {tab === 0 && (results ? (

              /* Résultats envoi */
              <div>
                <div style={{ textAlign:'center', marginBottom:28 }}>
                  <div style={{ fontSize:56, marginBottom:14 }}>{results.erreurs===0 ? '✅' : '⚠️'}</div>
                  <div style={{ fontSize:20, fontWeight:900, color:'#1a202c', marginBottom:6 }}>
                    {results.envoyes} lien{results.envoyes>1?'s':''} envoyé{results.envoyes>1?'s':''}
                  </div>
                  {results.erreurs > 0 && (
                    <div style={{ fontSize:14, color:'#dc3545', fontWeight:600 }}>{results.erreurs} erreur(s) d'envoi</div>
                  )}
                  <p style={{ fontSize:14, color:'#718096', marginTop:10, lineHeight:1.7 }}>
                    Chaque participant va recevoir un email avec son lien personnel et unique pour remplir le recueil.
                  </p>
                </div>

                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {(results.results||[]).map((r, i) => (
                    <div key={i} style={{
                      display:'flex', alignItems:'center', gap:12,
                      padding:'12px 16px', borderRadius:11,
                      background: r.statut==='envoye'?'rgba(25,135,84,.06)':r.statut==='deja_rempli'?'rgba(253,126,20,.06)':'rgba(220,53,69,.06)',
                      border:`1px solid ${r.statut==='envoye'?'rgba(25,135,84,.2)':r.statut==='deja_rempli'?'rgba(253,126,20,.2)':'rgba(220,53,69,.2)'}`,
                    }}>
                      <FaEnvelope style={{ color:r.statut==='envoye'?'#198754':r.statut==='deja_rempli'?'#fd7e14':'#dc3545', flexShrink:0, fontSize:14 }}/>
                      <span style={{ fontSize:14, color:'#374151', flex:1 }}>{r.email}</span>
                      <span style={{ fontSize:12, fontWeight:700,
                        color: r.statut==='envoye'?'#198754':r.statut==='deja_rempli'?'#fd7e14':'#dc3545' }}>
                        {r.statut==='envoye'?'Envoyé ✓':r.statut==='deja_rempli'?'Déjà rempli':'Erreur d\'envoi'}
                      </span>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop:20, padding:'13px 16px', background:'rgba(25,135,84,.05)', borderRadius:12, border:'1px solid rgba(25,135,84,.15)', fontSize:14, color:'#4a5568' }}>
                  💡 Suivez les réponses en temps réel dans l'onglet <strong>"Suivi des envois"</strong>.
                </div>
              </div>

            ) : (

              /* Formulaire saisie emails */
              <div>
                {globalErr && (
                  <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 16px', background:'rgba(220,53,69,.06)', border:'1px solid rgba(220,53,69,.2)', borderRadius:11, color:'#dc3545', fontSize:14, fontWeight:600, marginBottom:18 }}>
                    <FaExclamationCircle style={{ flexShrink:0 }}/> {globalErr}
                  </div>
                )}

                {/* Astuce */}
                <div style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'12px 16px', background:'rgba(13,110,253,.05)', border:'1px solid rgba(13,110,253,.15)', borderRadius:11, marginBottom:22, fontSize:13, color:'#4a5568', lineHeight:1.7 }}>
                  <FaInfoCircle style={{ color:'#0d6efd', flexShrink:0, marginTop:2 }}/>
                  <span>
                    <strong style={{ color:'#1a202c' }}>Saisie rapide :</strong> Vous pouvez coller une liste d'emails (un par ligne, ou séparés par virgule/point-virgule) directement dans le premier champ.
                  </span>
                </div>

                {/* Date expiration */}
                <div style={{ marginBottom:22 }}>
                  <label style={{ fontSize:13, fontWeight:700, color:'#374151', display:'block', marginBottom:8 }}>
                    <FaCalendarAlt style={{ marginRight:6, color:'#fd7e14', fontSize:11 }}/>
                    Date d'expiration des liens <span style={{ fontWeight:400, color:'#a0aec0' }}>(optionnel)</span>
                  </label>
                  <input type="date" value={expireAt} onChange={e => setExpireAt(e.target.value)}
                    style={{ height:44, padding:'0 14px', border:'2px solid #e2e8f0', borderRadius:10, fontSize:14, fontFamily:'inherit', color:'#1a202c', outline:'none', width:240, cursor:'pointer' }}/>
                  <p style={{ fontSize:12, color:'#a0aec0', marginTop:6 }}>Par défaut : 30 jours après l'envoi</p>
                </div>

                {/* Label champ emails */}
                <div style={{ fontSize:13, fontWeight:700, color:'#374151', marginBottom:10 }}>
                  <FaEnvelope style={{ marginRight:6, color:'#fd7e14', fontSize:11 }}/>
                  Adresses email des participants
                </div>

                {/* Champs email */}
                <div style={{ marginBottom:14 }}>
                  {emails.map((em, i) => (
                    <div key={i} className="email-row">
                      <input
                        className={`email-inp${errors[i]?' err':''}`}
                        type="email"
                        placeholder={`participant${i+1}@exemple.com`}
                        value={em}
                        onChange={e => updateEmail(i, e.target.value)}
                        onPaste={i===0 ? handlePasteArea : undefined}
                      />
                      <button className="sm-del" onClick={() => removeEmail(i)} disabled={emails.length===1}>
                        <FaTrash style={{ fontSize:11 }}/>
                      </button>
                    </div>
                  ))}
                </div>

                <button className="sm-add" onClick={addEmail}>
                  <FaPlus style={{ fontSize:11 }}/> Ajouter un email
                </button>

                {/* Résumé */}
                <div style={{ marginTop:20, padding:'12px 16px', background:'#f8f9fa', borderRadius:11, border:'1px solid #e2e8f0', fontSize:13, color:'#718096', lineHeight:1.7 }}>
                  <FaInfoCircle style={{ marginRight:6, color:'#a0aec0' }}/>
                  Chaque adresse recevra un <strong style={{ color:'#374151' }}>lien unique et personnel</strong>. Le participant remplira lui-même son nom, prénom et toutes ses informations dans le formulaire.
                </div>
              </div>
            ))}

            {/* TAB 1 — Suivi envois */}
            {tab === 1 && (
              loadLiens ? (
                <div style={{ textAlign:'center', padding:'48px 0' }}>
                  <FaSpinner className="smspin" style={{ fontSize:28, color:'#fd7e14', display:'block', margin:'0 auto 12px' }}/>
                  <p style={{ fontSize:14, color:'#718096' }}>Chargement des envois...</p>
                </div>
              ) : liens.length === 0 ? (
                <div style={{ textAlign:'center', padding:'56px 0' }}>
                  <div style={{ fontSize:48, marginBottom:14 }}>📭</div>
                  <p style={{ fontSize:15, fontWeight:700, color:'#374151', marginBottom:6 }}>Aucun lien envoyé pour cette session</p>
                  <p style={{ fontSize:14, color:'#718096' }}>Utilisez l'onglet "Envoyer les liens" pour commencer.</p>
                </div>
              ) : (
                <>
                  {/* Mini stats */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:20 }}>
                    {[
                      { l:'Total',   v:liens.length,                                   c:'#718096' },
                      { l:'Envoyés', v:liens.filter(l=>l.statut!=='en_attente').length, c:'#0d6efd' },
                      { l:'Ouverts', v:liens.filter(l=>l.statut==='ouvert').length,     c:'#fd7e14' },
                      { l:'Soumis',  v:liens.filter(l=>l.statut==='soumis').length,     c:'#198754' },
                    ].map((s, i) => (
                      <div key={i} style={{ padding:'12px 16px', borderRadius:11, border:'1px solid #e2e8f0', background:'#fafafa' }}>
                        <div style={{ fontSize:10.5, fontWeight:700, textTransform:'uppercase', color:'#a0aec0', marginBottom:4 }}>{s.l}</div>
                        <div style={{ fontSize:24, fontWeight:900, color:s.c }}>{s.v}</div>
                      </div>
                    ))}
                  </div>

                  {/* Liste liens */}
                  {liens.map(l => (
                    <div key={l.id} className="lien-row">
                      <div style={{ width:36, height:36, borderRadius:9, background:'#f0f3f8', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <FaEnvelope style={{ color:'#718096', fontSize:13 }}/>
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:14, fontWeight:600, color:'#1a202c', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {l.email}
                        </div>
                        {l.envoye_at && (
                          <div style={{ fontSize:11.5, color:'#a0aec0', marginTop:1 }}>
                            Envoyé le {new Date(l.envoye_at).toLocaleDateString('fr-FR')}
                            {l.nb_relances > 0 && ` · ${l.nb_relances} relance${l.nb_relances>1?'s':''}`}
                          </div>
                        )}
                      </div>
                      <div style={{ flexShrink:0 }}>{badge(l.statut)}</div>
                      {l.statut !== 'soumis' ? (
                        <div style={{ flexShrink:0, marginLeft:8 }}>
                          {relId === l.id ? (
                            <FaSpinner className="smspin" style={{ color:'#fd7e14', fontSize:15 }}/>
                          ) : (
                            <button onClick={() => relance(l.id)} style={{
                              display:'flex', alignItems:'center', gap:6, padding:'6px 12px',
                              border:'1.5px solid rgba(253,126,20,.3)', borderRadius:8,
                              background:'rgba(253,126,20,.06)', color:'#fd7e14',
                              cursor:'pointer', fontSize:12.5, fontWeight:700, fontFamily:'inherit',
                            }}>
                              <FaRedo style={{ fontSize:10 }}/> Relancer
                            </button>
                          )}
                          {relMsg?.id === l.id && (
                            <div style={{ fontSize:11, fontWeight:600, marginTop:3, textAlign:'right',
                              color: relMsg.t==='ok'?'#198754':'#dc3545' }}>
                              {relMsg.m}
                            </div>
                          )}
                        </div>
                      ) : (
                        <FaCheckCircle style={{ color:'#198754', fontSize:18, flexShrink:0, marginLeft:8 }}/>
                      )}
                    </div>
                  ))}
                </>
              )
            )}

          </div>

          {/*  FOOTER ─ */}
          <div style={{ padding:'16px 24px', borderTop:'2px solid #f0f4f8', display:'flex', justifyContent:'space-between', alignItems:'center', background:'#fafbfc', flexShrink:0 }}>
            <div style={{ fontSize:13, color:'#a0aec0' }}>
              {tab===0 && !results && `${emails.filter(e=>e.trim()).length} email${emails.filter(e=>e.trim()).length>1?'s':''} à notifier`}
              {tab===0 &&  results && `${results.envoyes} envoyé${results.envoyes>1?'s':''}`}
              {tab===1 && `${liens.filter(l=>l.statut==='soumis').length} / ${liens.length} recueils reçus`}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={onClose} style={{ padding:'11px 22px', border:'2px solid #e2e8f0', borderRadius:10, background:'transparent', cursor:'pointer', fontSize:14, fontWeight:700, color:'#718096', fontFamily:'inherit' }}>
                {results ? 'Fermer' : 'Annuler'}
              </button>
              {tab===0 && !results && (
                <button onClick={send} disabled={loading} style={{
                  display:'flex', alignItems:'center', gap:9, padding:'11px 26px',
                  border:'none', borderRadius:10,
                  background: loading ? '#a0aec0' : 'linear-gradient(135deg,#fd7e14,#e8700a)',
                  color:'#fff', cursor: loading?'not-allowed':'pointer',
                  fontSize:14, fontWeight:800, fontFamily:'inherit',
                  boxShadow: loading ? 'none' : '0 6px 20px rgba(253,126,20,.3)',
                  transition:'all .2s',
                }}>
                  {loading
                    ? <><FaSpinner className="smspin" style={{ fontSize:13 }}/> Envoi en cours...</>
                    : <><FaPaperPlane style={{ fontSize:12 }}/> Envoyer {emails.filter(e=>e.trim()).length} lien{emails.filter(e=>e.trim()).length>1?'s':''}</>
                  }
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default SendRecueilModal;